import { MouseEvent, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { AssistantIndicator } from './AssistantIndicator';
import { UpgradeFlag } from './UpgradeFlag';

import { ROUTE_PATHS } from '@/config/constants';
import { useRouteInfo } from '@/hooks/useRouteInfo';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface NavigationItemProps {
  to: (typeof ROUTE_PATHS)[keyof typeof ROUTE_PATHS];
  isAssistant?: boolean;
  isInBeta?: boolean;
  children: ReactNode;
}

export function NavigationLink({
  to,
  isAssistant = false,
  isInBeta = false,
  children,
}: NavigationItemProps) {
  const { pathname } = useLocation();

  const routeInfo = useRouteInfo(to);
  const enabled = !!routeInfo?.enabled;

  function handleClick(
    event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
  ) {
    if (!enabled) {
      event.preventDefault();
    }
  }

  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'relative flex items-center gap-2 rounded-lg px-5 py-2 text-[#333] hover:bg-[#E8E8E8]',

            isActive &&
              enabled &&
              'cursor-default bg-black text-white hover:bg-black',

            !enabled && 'cursor-default hover:bg-white',
          )
        }
        onClick={handleClick}
      >
        {children}

        {isInBeta && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-[#E1F0F2] px-1 text-xs text-black">
            {i18n.t(
              'components.NavigationBar.components.NavigationLink.betaFlag',
            )}
          </span>
        )}

        {isAssistant && pathname === ROUTE_PATHS.HOME && <AssistantIndicator />}

        {!enabled && <UpgradeFlag />}
      </NavLink>
    </li>
  );
}
